// One expert's profile at /experts/<slug> (SFLANDING-120). Everything on the
// page comes from the List of RDs row in Airtable — headline, bio, links,
// featured article, quote — so Claire edits it there, not here. The copy was
// ported from the old site's RD pages; the "use code CHERRY" promo line those
// pages carried was dropped on purpose.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { COLORS, HEADING, TYPE } from 'styles/theme';
import { PAD, WIDTH, SPACE, RADIUS, SURFACE, BUTTON, BUTTON_OUTLINE, container } from 'styles/blocks';
import LightMarkup from 'components/marketing/LightMarkup';

const API = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';

const initials = (name) => name.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('');
const paragraphs = (text) => String(text || '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

// Loads the one expert whose slug is in the URL. Shared by the profile and
// the hosted-article view below.
function useExpert(slug) {
  const [state, setState] = useState({ loading: true, expert: null });
  useEffect(() => {
    let alive = true;
    setState({ loading: true, expert: null });
    fetch(`${API}/partners`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!alive) return;
        const expert = (data?.partners || []).find((p) => p.slug === slug) || null;
        setState({ loading: false, expert });
      })
      .catch(() => { if (alive) setState({ loading: false, expert: null }); });
    return () => { alive = false; };
  }, [slug]);
  return state;
}

const backLinkStyle = { color: COLORS.dark, textDecoration: 'underline', fontWeight: 600, fontSize: TYPE.bodySm };

function Loading() {
  return (
    <div style={{ ...container(WIDTH.prose), padding: `${SPACE.bandY}px ${PAD}px` }}>
      <p style={{ color: COLORS.muted, fontSize: TYPE.body }}>Loading…</p>
    </div>
  );
}

function NotFound() {
  return (
    <div style={{ ...container(WIDTH.prose), padding: `${SPACE.bandY}px ${PAD}px`, textAlign: 'center' }}>
      <h1 style={{ ...HEADING, fontSize: TYPE.displayHeading, margin: '0 0 16px' }}>We couldn't find that expert.</h1>
      <p style={{ fontSize: TYPE.body, color: COLORS.text, margin: '0 0 24px' }}>They may have moved, or the link is out of date.</p>
      <Link to="/experts" style={backLinkStyle}>← All experts</Link>
    </div>
  );
}

/** /experts/<slug>/article — the featured article, when Claire pastes its text into Airtable. */
export function ExpertArticlePage() {
  const { slug } = useParams();
  const { loading, expert: e } = useExpert(slug);
  if (loading) return <Loading />;
  if (!e || !e.article?.content) return <NotFound />;
  return (
    <article style={{ ...container(WIDTH.prose), padding: `${SPACE.bandTop}px ${PAD}px ${SPACE.bandY}px` }}>
      <Link to={`/experts/${e.slug}`} style={backLinkStyle}>← {e.name}</Link>
      <p style={{ fontSize: TYPE.bodySm, color: COLORS.muted, margin: '28px 0 8px', textTransform: 'uppercase', letterSpacing: '1px' }}>
        By {e.name}{e.credentials ? `, ${e.credentials}` : ''}
      </p>
      <h1 style={{ ...HEADING, fontSize: TYPE.displayHeading, lineHeight: 1.15, margin: '0 0 28px' }}>{e.article.title}</h1>
      <LightMarkup text={e.article.content} />
    </article>
  );
}

export default function ExpertProfilePage() {
  const { slug } = useParams();
  const { dark, text, muted } = COLORS;
  const state = useExpert(slug);

  const back = <Link to="/experts" style={backLinkStyle}>← All experts</Link>;

  if (state.loading) return <Loading />;
  if (!state.expert) return <NotFound />;

  const e = state.expert;
  const bio = paragraphs(e.profileBio || e.bio);
  const nameBlock = e.website
    ? <a href={e.website} target="_blank" rel="noreferrer" style={{ color: dark, textDecoration: 'none' }}>{e.name}</a>
    : e.name;

  return (
    <div>
      {/* Header: photo beside name, credentials, headline. Wraps on phones. */}
      <section style={{ ...container(WIDTH.quote), padding: `${SPACE.bandTop}px ${PAD}px ${SPACE.blockY}px` }}>
        <div style={{ marginBottom: '28px' }}>{back}</div>
        <div style={{ display: 'flex', gap: '40px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{
            width: '220px', height: '220px', borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
            backgroundColor: '#eee6d6', display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#b3a687', ...HEADING, fontSize: TYPE.sectionHeading,
          }}>
            {e.photo
              ? <img src={e.photo} alt={e.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              : initials(e.name)}
          </div>
          <div style={{ flex: '1 1 320px' }}>
            <h1 style={{ ...HEADING, fontSize: TYPE.displayHeading, lineHeight: 1.1, margin: '0 0 6px' }}>{nameBlock}</h1>
            {e.credentials && (
              <p style={{ fontSize: TYPE.body, color: text, margin: '0 0 4px', letterSpacing: '0.5px' }}>{e.credentials}</p>
            )}
            {e.headline && (
              <p style={{ fontSize: TYPE.body, color: muted, margin: 0 }}>{e.headline}</p>
            )}
            {e.contactUrl && (
              <a href={e.contactUrl} target="_blank" rel="noreferrer" style={{ ...BUTTON, marginTop: '22px' }}>
                Contact {e.name.split(' ')[0]}
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Bio */}
      {bio.length > 0 && (
        <section style={{ ...container(WIDTH.prose), padding: `0 ${PAD}px ${SPACE.bandY}px` }}>
          {bio.map((p, i) => (
            <p key={i} style={{ fontSize: TYPE.body, lineHeight: 1.85, color: text, margin: '0 0 18px' }}>{p}</p>
          ))}
        </section>
      )}

      {/* Featured article — only when the row has one */}
      {e.article && (
        <section style={{ background: COLORS.creamAlt, padding: `${SPACE.bandY}px ${PAD}px` }}>
          <div style={{ ...container(WIDTH.prose), padding: 0 }}>
            <p style={{ fontSize: TYPE.bodySm, color: muted, margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Featured by {e.name.split(' ')[0]}
            </p>
            <h2 style={{ ...HEADING, fontSize: TYPE.bandHeading, margin: '0 0 12px' }}>{e.article.title}</h2>
            {e.article.blurb && (
              <p style={{ fontSize: TYPE.body, lineHeight: 1.75, color: text, margin: '0 0 22px' }}>{e.article.blurb}</p>
            )}
            {/* Hosted here when Article Content is filled in Airtable; otherwise
                out to wherever the article lives. */}
            {e.article.content
              ? <Link to={`/experts/${e.slug}/article`} style={BUTTON_OUTLINE}>Read more</Link>
              : e.article.url && (
                <a href={e.article.url} target="_blank" rel="noreferrer" style={BUTTON_OUTLINE}>Read more</a>
              )}
          </div>
        </section>
      )}

      {/* What they say about Square Fare */}
      {e.quote && (
        <section style={{ padding: `${SPACE.bandY}px ${PAD}px` }}>
          <blockquote style={{ maxWidth: `${WIDTH.measure}px`, margin: '0 auto', textAlign: 'center' }}>
            <p style={{ ...HEADING, fontSize: TYPE.quote, lineHeight: 1.7, fontStyle: 'italic', color: dark, margin: '0 0 16px' }}>
              “{e.quote}”
            </p>
            <footer style={{ fontSize: TYPE.bodySm, fontWeight: 700, color: text }}>
              — {e.name}{e.credentials ? `, ${e.credentials}` : ''}
            </footer>
          </blockquote>
        </section>
      )}

      {/* Where to next */}
      <section style={{ textAlign: 'center', padding: `${SPACE.blockY}px ${PAD}px ${SPACE.bandY}px` }}>
        <div style={{ background: SURFACE.cardBg, border: SURFACE.rule, borderRadius: `${RADIUS.band}px`, padding: '32px 24px', maxWidth: `${WIDTH.single}px`, margin: '0 auto' }}>
          <h2 style={{ ...HEADING, fontSize: TYPE.bandHeading, margin: '0 0 10px' }}>Working with {e.name.split(' ')[0]}?</h2>
          <p style={{ fontSize: TYPE.body, lineHeight: 1.7, color: text, margin: '0 0 20px' }}>
            Take the quiz and we'll cook to the plan you build together.
          </p>
          <Link to="/quiz" style={BUTTON}>Take the Quiz</Link>
        </div>
      </section>
    </div>
  );
}
